/**
 * Univer sheet event handlers: BeforeCommandExecute, CommandExecuted, Undo, Redo, SheetRangeSorted, ClipboardPasted.
 * Extracted from Spreadsheet.tsx to keep the main component smaller.
 */

import { CellValueType } from '@univerjs/core';
import { FUniver } from '@univerjs/core/facade';
import {
  deleteCells,
  displayColumnName,
  getCells,
  isLockedColumn,
  isPrimaryColumnName,
  putCells,
  PRIMARY_COLUMN_NAMES
} from '../../api/spreadsheet';
import type { Cell, Column } from '../../api/spreadsheet';
import type { CellValidationError } from '../../utils/spreadsheetValidation';
import { validateCell } from '../../utils/spreadsheetValidation';
import { VALID_CELL_STYLE } from '../../utils/spreadsheetValidation';
import {
  applyColumnBlockMoveInPlace,
  buildOldToNewColumnIndexMap,
  getRightmostPrimaryColumnIndex,
  type ColumnSchemaWithData,
  LOCKED_COLUMN_ERROR_MSG,
  MAX_SPREADSHEET_COLUMNS,
  MAX_SPREADSHEET_ROWS
} from './spreadsheetUtils';
import type {
  ReapplyValidationStylesOptions,
  SpreadsheetCellClickedContext,
  UniverEventsContext
} from './spreadsheetTypes';
import {
  addSpreadsheetDirtyFullSheet,
  addSpreadsheetDirtyRange
} from './spreadsheetSyncBuffer';

function getRangeFromValue(value: unknown): {
  startRow: number;
  startColumn: number;
  endRow: number;
  endColumn: number;
} | null {
  if (value == null) return null;
  if (Array.isArray(value)) {
    const rows = value.length;
    const cols = rows > 0 && Array.isArray(value[0]) ? value[0].length : 0;
    return {
      startRow: 0,
      startColumn: 0,
      endRow: Math.max(0, rows - 1),
      endColumn: Math.max(0, cols - 1)
    };
  }
  if (typeof value === 'object' && !Array.isArray(value)) {
    const rowKeys = Object.keys(value)
      .map(Number)
      .filter((n) => !Number.isNaN(n));
    if (rowKeys.length === 0) return null;
    const minRow = Math.min(...rowKeys);
    const maxRow = Math.max(...rowKeys);
    let minCol = Infinity;
    let maxCol = -Infinity;
    for (const r of rowKeys) {
      const rowObj = (value as Record<number, unknown>)[r];
      if (
        rowObj != null &&
        typeof rowObj === 'object' &&
        !Array.isArray(rowObj)
      ) {
        const colKeys = Object.keys(rowObj)
          .map(Number)
          .filter((n) => !Number.isNaN(n));
        for (const c of colKeys) {
          minCol = Math.min(minCol, c);
          maxCol = Math.max(maxCol, c);
        }
      }
    }
    if (maxCol < 0) return null;
    return {
      startRow: minRow,
      startColumn: minCol,
      endRow: maxRow,
      endColumn: maxCol
    };
  }
  return null;
}

function getCellRawValueAt(
  value:
    | { v?: unknown }
    | unknown[][]
    | Record<number, Record<number, { v?: unknown }>>
    | number
    | string
    | boolean,
  range: {
    startRow: number;
    startColumn: number;
    endRow: number;
    endColumn: number;
  },
  row: number,
  col: number
): unknown {
  if (value == null) return null;
  const isSingleCell =
    range.startRow === range.endRow &&
    range.startColumn === range.endColumn &&
    row === range.startRow &&
    col === range.startColumn;
  if (
    isSingleCell &&
    (typeof value === 'number' ||
      typeof value === 'string' ||
      typeof value === 'boolean')
  ) {
    return value;
  }
  let cell: unknown = null;
  if (Array.isArray(value)) {
    const r = row - range.startRow;
    const c = col - range.startColumn;
    const rowArr = value[r];
    if (Array.isArray(rowArr) && rowArr[c] != null) {
      cell = rowArr[c];
    }
  } else {
    const rowObj = (value as Record<number, unknown>)[row];
    if (
      rowObj != null &&
      typeof rowObj === 'object' &&
      !Array.isArray(rowObj)
    ) {
      const cellData = (rowObj as Record<number, { v?: unknown }>)[col];
      cell = cellData ?? null;
    }
    if (
      cell == null &&
      row === range.startRow &&
      col === range.startColumn &&
      range.startRow === range.endRow &&
      range.startColumn === range.endColumn &&
      typeof value === 'object'
    ) {
      cell = value;
    }
  }
  return cell;
}

function rangeIncludesColumn(
  colIndex: number,
  range: { startColumn?: number; endColumn?: number }
): boolean {
  if (colIndex < 0) return false;
  const s = range.startColumn ?? 0;
  const e = range.endColumn ?? 0;
  return s <= colIndex && e >= colIndex;
}

type HeaderStateForReconcile = {
  columnNamesByIndex: string[];
  columns: Column[];
  getUniqueColumnName: (base: string, existing: string[]) => string;
} | null;

/**
 * When Univer has more physical columns than `columnNamesByIndex` (e.g. multi-column paste without insert-col),
 * append column schema + names so sync and dirty-marking include all pasted cells.
 */
function reconcileHeaderColumnsToUniverCount(
  univerAPI: ReturnType<typeof FUniver.newAPI>,
  headerStateRef: { current: HeaderStateForReconcile },
  normalizeColumnIndicesFn: (cols: Column[]) => void
): number {
  const state = headerStateRef.current;
  if (!state?.columnNamesByIndex || !state.getUniqueColumnName) return 0;
  const sheet = univerAPI.getActiveWorkbook()?.getActiveSheet()?.getSheet?.();
  const univerCount = sheet?.getConfig?.()?.columnCount ?? 0;
  if (univerCount <= 0) return 0;
  const target = Math.min(univerCount, MAX_SPREADSHEET_COLUMNS);
  const names = state.columnNamesByIndex;
  let added = 0;
  while (names.length < target) {
    const i = names.length;
    const baseName = 'Column ' + i;
    const name = state.getUniqueColumnName(baseName, names) ?? baseName;
    names.push(name);
    if (state.columns) {
      state.columns.push({
        col_name: name,
        col_idx: i,
        col_type: 'str',
        description: ''
      } as Column);
    }
    added += 1;
  }
  if (added > 0 && state.columns) normalizeColumnIndicesFn(state.columns);
  return added;
}

/** Clipboard paste completes with these commands (optional-paste is omitted — it also runs after insert-col and caused duplicate columns). */
const SHEET_PASTE_COMMAND_IDS: readonly string[] = [
  'sheet.command.paste',
  'sheet.command.paste-value'
];

/**
 * After insert-col (and similar), Univer runs follow-up mutations while `addColumnInProgressRef` may already be false.
 * Suppress trailing-column reconcile briefly so we do not append duplicate `columnNamesByIndex` / backend columns.
 */
let suppressTrailingColumnAlignUntilMs = 0;
const SUPPRESS_TRAILING_ALIGN_AFTER_COL_INSERT_MS = 650;

/**
 * Re-applies Univer cell value type and validation styles for columns that have schema overrides.
 * Call when columnSchemaOverrides change (e.g. from the columnSchemaOverrides useEffect).
 */
export function reapplyValidationStylesForColumnOverrides(
  options: ReapplyValidationStylesOptions
): void {
  const {
    api,
    headerStateRef,
    columnSchemaOverridesRef,
    validationErrorsMapRef,
    setValidationErrorsRef,
    isApplyingValidationStylesRef,
    requestSheetRepaint,
    getColTypeFromSchema,
    normalizeCellValue,
    cellValueToUniverCell
  } = options;
  const state = headerStateRef.current;
  const overrides = columnSchemaOverridesRef.current;
  if (
    !api ||
    !state?.columns ||
    !overrides ||
    Object.keys(overrides).length === 0
  )
    return;
  const fWorksheet = api.getActiveWorkbook()?.getActiveSheet();
  if (!fWorksheet) return;
  const sheet = fWorksheet.getSheet();
  const rowCount = sheet.getConfig().rowCount ?? 0;
  if (rowCount <= 0) return;
  const columnNamesByIndex = state.columnNamesByIndex ?? [];
  for (const idxStr of Object.keys(overrides)) {
    const col = parseInt(idxStr, 10);
    if (Number.isNaN(col) || col < 0 || col >= state.columns.length) continue;
    const colType = getColTypeFromSchema(
      state.columns[col] as ColumnSchemaWithData | undefined
    );
    const fRange = fWorksheet.getRange(0, col, rowCount, 1);
    const grid = fRange.getRawValues?.() ?? fRange.getValues();
    const map = new Map(validationErrorsMapRef.current);
    Array.from(map.keys()).forEach((key) => {
      if (key.endsWith(`,${col}`)) map.delete(key);
    });
    const valueObj: Record<
      number,
      Record<
        number,
        { v: unknown; t: CellValueType; s?: Record<string, unknown> | null }
      >
    > = {};
    for (let row = 0; row < (grid?.length ?? 0); row++) {
      const v = grid?.[row]?.[0];
      if (v == null || v === '') continue;
      const cellValue = normalizeCellValue(v);
      const result = validateCell(cellValue, colType);
      if (!result.valid && result.message) {
        const err: CellValidationError = {
          row,
          col,
          columnName: columnNamesByIndex[col] ?? `Column ${col}`,
          value: cellValue.slice(0, 50) + (cellValue.length > 50 ? '…' : ''),
          message: result.message
        };
        map.set(`${row},${col}`, err);
      }
      if (!valueObj[row]) valueObj[row] = {};
      valueObj[row][col] = cellValueToUniverCell(cellValue, {
        colType,
        ...(map.has(`${row},${col}`) ? {} : { s: VALID_CELL_STYLE })
      });
    }
    if (Object.keys(valueObj).length > 0) {
      const range = {
        startRow: 0,
        startColumn: col,
        endRow: rowCount - 1,
        endColumn: col
      };
      isApplyingValidationStylesRef.current = true;
      api.executeCommand('sheet.command.set-range-values', {
        range,
        value: valueObj
      });
      isApplyingValidationStylesRef.current = false;
    }
    validationErrorsMapRef.current = map;
    setValidationErrorsRef.current?.(Array.from(map.values()));
    requestSheetRepaint?.();
  }
}

/**
 * Registers Univer sheet event handlers (BeforeCommandExecute, CommandExecuted, Undo, Redo, etc.).
 * Call once after creating the Univer API inside the sheet useEffect.
 */
export function registerUniverEvents(
  univerAPI: ReturnType<typeof FUniver.newAPI>,
  ctx: UniverEventsContext
): void {
  const {
    campaignId,
    viewType,
    canEditRef,
    showSnackbarRef,
    showSheetLockedModalRef,
    prospectPrioritizationActiveRef,
    headerStateRef,
    addColumnInProgressRef,
    addRowInProgressRef,
    validationErrorsMapRef,
    setValidationErrorsRef,
    requestSheetRepaint,
    onColumnsChangedRef,
    syncDebounceTimerRef,
    scheduleDebouncedSyncRef,
    dbTriggeredSyncPendingRef,
    syncBufferRef,
    rowOperationPromiseRef,
    isApplyingValidationStylesRef,
    isApplyingEnrichmentCellRef,
    isApplyingSyncFromBackendRef,
    enrichmentLockedRef,
    skipInsertColBeforeRef,
    skipInsertColCreateRef,
    applyBackendColumnReorderInProgressRef,
    syncOnCellEditDebounceMs,
    getColTypeFromSchema,
    normalizeCellValue,
    cellValueToUniverCell,
    ensureDomainForRowsInPayload,
    syncSheetCellsToBackend,
    normalizeColumnIndices,
    onBackendOnlyColumns,
    applySheetColumnOrderFromBackend,
    enrichingCellsRef,
    setEnrichingCellsCount,
    prioritizingCellsRef,
    setPrioritizingCellsCount,
    spreadsheetBackendSyncPhaseSetterRef
  } = ctx;

  univerAPI.addEvent(univerAPI.Event.BeforeCommandExecute, (event) => {
    if (!canEditRef.current) {
      // Allow programmatic updates when sheet is locked: sync, enrichment, validation re-apply, or prioritization.
      const programmaticUpdate =
        isApplyingSyncFromBackendRef?.current ||
        isApplyingEnrichmentCellRef?.current ||
        isApplyingValidationStylesRef?.current;
      if (!programmaticUpdate) {
        const viewOnlyCommands = [
          'sheet.command.insert-row',
          'sheet.command.insert-row-by-range',
          'sheet.command.insert-row-before',
          'sheet.command.insert-row-after',
          'sheet.command.insert-multi-rows-above',
          'sheet.command.insert-multi-rows-after',
          'sheet.command.insert-col',
          'sheet.command.insert-col-by-range',
          'sheet.command.insert-col-before',
          'sheet.command.insert-col-after',
          'sheet.command.insert-multi-cols-before',
          'sheet.command.insert-multi-cols-right',
          'sheet.command.remove-row',
          'sheet.command.remove-col',
          'sheet.command.set-range-values',
          'sheet.command.delete-range',
          'sheet.command.clear-selection-content',
          'sheet.mutation.set-range-values'
        ];
        if (
          viewOnlyCommands.includes(event.id) ||
          event.id === 'sheet.command.move-cols' ||
          event.id === 'sheet.command.reorder-range'
        ) {
          event.cancel = true;
          addColumnInProgressRef.current = false;
          addRowInProgressRef.current = false;
          if (!prospectPrioritizationActiveRef?.current) {
            showSheetLockedModalRef?.current?.();
          }
          return;
        }
      }
    }
    const state = headerStateRef.current;
    const columnNamesByIndex = state?.columnNamesByIndex ?? [];
    const lockedColumnIndices = columnNamesByIndex
      .map((name, i) => (isLockedColumn(name) ? i : -1))
      .filter((i) => i >= 0);
    const primaryColumnIndices = columnNamesByIndex
      .map((name, i) => (isPrimaryColumnName(name) ? i : -1))
      .filter((i) => i >= 0);
    const { id, params } = event;

    const rowInsertCommands = [
      'sheet.command.insert-row',
      'sheet.command.insert-row-by-range',
      'sheet.command.insert-row-before',
      'sheet.command.insert-row-after',
      'sheet.command.insert-multi-rows-above',
      'sheet.command.insert-multi-rows-after'
    ];
    if (rowInsertCommands.includes(id)) {
      const sheet = univerAPI.getActiveWorkbook()?.getActiveSheet()?.getSheet();
      const currentRows = sheet?.getConfig()?.rowCount ?? 0;
      const numAdding =
        typeof params?.value === 'number'
          ? Math.max(0, params.value)
          : params?.range != null
            ? Math.max(
                0,
                (params.range.endRow ?? params.range.startRow ?? 0) -
                  (params.range.startRow ?? 0) +
                  1
              )
            : 1;
      if (currentRows + numAdding > MAX_SPREADSHEET_ROWS) {
        event.cancel = true;
        addRowInProgressRef.current = false;
        showSnackbarRef.current?.(
          `Spreadsheet is limited to ${MAX_SPREADSHEET_ROWS} rows.`,
          'warning'
        );
        return;
      }
    }

    const colInsertCommands = [
      'sheet.command.insert-col',
      'sheet.command.insert-col-by-range',
      'sheet.command.insert-col-before',
      'sheet.command.insert-col-after',
      'sheet.command.insert-multi-cols-before',
      'sheet.command.insert-multi-cols-right'
    ];
    if (colInsertCommands.includes(id)) {
      const numAdding =
        typeof params?.value === 'number'
          ? Math.max(0, params.value)
          : params?.range != null
            ? Math.max(
                0,
                (params.range.endColumn ?? params.range.startColumn ?? 0) -
                  (params.range.startColumn ?? 0) +
                  1
              )
            : 1;
      if (columnNamesByIndex.length + numAdding > MAX_SPREADSHEET_COLUMNS) {
        event.cancel = true;
        addColumnInProgressRef.current = false;
        showSnackbarRef.current?.(
          `Spreadsheet is limited to ${MAX_SPREADSHEET_COLUMNS} columns.`,
          'warning'
        );
        return;
      }
    }

    if (id === 'sheet.command.remove-col' && params?.range) {
      const startCol = params.range.startColumn ?? 0;
      const endCol = params.range.endColumn ?? 0;
      PRIMARY_COLUMN_NAMES.forEach((primaryColName) => {
        const primaryColIndex = columnNamesByIndex.indexOf(primaryColName);
        if (
          primaryColIndex >= 0 &&
          startCol <= primaryColIndex &&
          endCol >= primaryColIndex
        ) {
          event.cancel = true;
          showSnackbarRef.current?.(
            `The ${displayColumnName(primaryColName)} column cannot be deleted.`,
            'error'
          );
          return;
        }
      });
    } else if (id === 'sheet.command.insert-col' && params?.range) {
      const range = params.range as {
        startColumn?: number;
        endColumn?: number;
      };
      const numAdded =
        typeof params?.value === 'number'
          ? Math.max(0, params.value)
          : Math.max(
              0,
              (range.endColumn ?? range.startColumn ?? 0) -
                (range.startColumn ?? 0) +
                1
            );
      const startCol = range.startColumn ?? 0;
      if (skipInsertColBeforeRef?.current) {
        skipInsertColBeforeRef.current = false;
        // Caller already updated columnNamesByIndex/columns (e.g. from backend); don't splice default names.
      } else {
        const rightmostPrimaryIndex = Math.max(
          -1,
          ...PRIMARY_COLUMN_NAMES.map((n) =>
            columnNamesByIndex.indexOf(n)
          ).filter((i) => i >= 0)
        );
        if (rightmostPrimaryIndex >= 0 && startCol <= rightmostPrimaryIndex) {
          event.cancel = true;
          showSnackbarRef.current?.(
            'New columns cannot be inserted to the left of the primary columns.',
            'error'
          );
          return;
        }
        for (let j = 0; j < numAdded; ++j) {
          const i = startCol + j;
          const baseName = 'Column ' + i;
          const name =
            state?.getUniqueColumnName(baseName, columnNamesByIndex) ??
            baseName;
          columnNamesByIndex.splice(i, 0, name);
          if (state?.columns)
            state.columns.splice(i, 0, {
              col_name: name,
              col_idx: i,
              col_type: 'str',
              description: ''
            });
        }
        if (state?.columns) normalizeColumnIndices(state.columns);
        headerStateRef.current?.refreshHeaderBar?.();
      }
    } else if (
      id === 'sheet.command.move-cols' &&
      params?.fromRange != null &&
      params?.toRange != null
    ) {
      const fromR = params.fromRange as {
        startColumn?: number;
        endColumn?: number;
      };
      const toR = params.toRange as {
        startColumn?: number;
        endColumn?: number;
      };
      const fromS = fromR.startColumn ?? 0;
      const fromE = fromR.endColumn ?? fromS;
      const insertAt = toR.startColumn ?? 0;
      for (let c = fromS; c <= fromE; c++) {
        const colName = columnNamesByIndex[c] ?? '';
        if (isPrimaryColumnName(colName)) {
          event.cancel = true;
          showSnackbarRef.current?.(
            `The ${displayColumnName(colName)} column cannot be moved.`,
            'error'
          );
          return;
        }
      }
      const rpm = getRightmostPrimaryColumnIndex(columnNamesByIndex);
      if (rpm >= 0 && insertAt < rpm + 1) {
        event.cancel = true;
        showSnackbarRef.current?.(
          'Columns cannot be moved into or before the primary columns.',
          'error'
        );
        return;
      }
    } else if (id === 'sheet.command.reorder-range' && params?.range) {
      const range = params.range as {
        startRow?: number;
        endRow?: number;
        startColumn?: number;
        endColumn?: number;
      };
      const order = params.order as Record<number, number> | undefined;
      const orderKeys = order
        ? Object.keys(order)
            .map(Number)
            .filter((n) => !Number.isNaN(n))
        : [];
      const startRow = range.startRow ?? 0;
      const endRow = range.endRow ?? 0;
      const isRowReorder =
        orderKeys.length > 0 &&
        endRow >= startRow &&
        orderKeys.every((k) => k >= startRow && k <= endRow);
      if (
        !isRowReorder &&
        primaryColumnIndices.some((idx) =>
          rangeIncludesColumn(idx, params.range)
        )
      ) {
        event.cancel = true;
        showSnackbarRef.current?.(
          'Primary columns cannot be reordered.',
          'error'
        );
      }
    } else if (
      id === 'sheet.command.move-range' &&
      params?.fromRange != null &&
      params?.toRange != null
    ) {
      if (
        lockedColumnIndices.some(
          (lockedIdx) =>
            rangeIncludesColumn(lockedIdx, params.fromRange) ||
            rangeIncludesColumn(lockedIdx, params.toRange)
        )
      ) {
        event.cancel = true;
        showSnackbarRef.current?.(LOCKED_COLUMN_ERROR_MSG, 'error');
      }
    } else if (
      id === 'sheet.command.set-col-hidden' &&
      Array.isArray(params?.ranges)
    ) {
      if (
        lockedColumnIndices.some((lockedIdx) =>
          params.ranges.some(
            (r: { startColumn?: number; endColumn?: number }) =>
              rangeIncludesColumn(lockedIdx, r)
          )
        )
      ) {
        event.cancel = true;
        showSnackbarRef.current?.(LOCKED_COLUMN_ERROR_MSG, 'error');
      }
    } else if (id === 'sheet.command.move-rows') {
      event.cancel = true;
    } else if (
      canEditRef.current &&
      (id === 'sheet.command.sort-range-asc-ext-ctx' ||
        id === 'sheet.command.sort-range-desc-ext-ctx')
    ) {
      event.cancel = true;
      const asc = id === 'sheet.command.sort-range-asc-ext-ctx';
      const fSheet = univerAPI.getActiveWorkbook()?.getActiveSheet();
      const activeRange = fSheet?.getActiveRange?.();
      const rangeData =
        typeof activeRange?.getRange === 'function'
          ? activeRange.getRange()
          : null;
      if (
        fSheet &&
        rangeData &&
        typeof rangeData === 'object' &&
        'startColumn' in rangeData
      ) {
        const colIndex =
          (rangeData as { startColumn?: number }).startColumn ?? 0;
        fSheet.sort(colIndex, asc);
      }
    } else if (
      canEditRef.current &&
      (id === 'sheet.command.sort-range-asc-ext' ||
        id === 'sheet.command.sort-range-desc-ext')
    ) {
      event.cancel = true;
      const asc = id === 'sheet.command.sort-range-asc-ext';
      const fSheet = univerAPI.getActiveWorkbook()?.getActiveSheet();
      const activeRange = fSheet?.getActiveRange?.();
      const rangeData =
        typeof activeRange?.getRange === 'function'
          ? activeRange.getRange()
          : null;
      if (
        fSheet &&
        rangeData &&
        typeof rangeData === 'object' &&
        'startColumn' in rangeData
      ) {
        const colIndex =
          (rangeData as { startColumn?: number }).startColumn ?? 0;
        fSheet.sort(colIndex, asc);
      }
    }
  });

  univerAPI.addEvent(univerAPI.Event.CommandExecuted, (event) => {
    const { id, params } = event;
    const state = headerStateRef.current;
    const columnNamesByIndex = state?.columnNamesByIndex ?? [];
    const columns = state?.columns ?? [];

    const colInsertExecutedIds = [
      'sheet.command.insert-col',
      'sheet.command.insert-col-by-range',
      'sheet.command.insert-col-before',
      'sheet.command.insert-col-after',
      'sheet.command.insert-multi-cols-before',
      'sheet.command.insert-multi-cols-right'
    ];

    if (id === 'sheet.command.insert-col') {
      const startCol = params.range.startColumn;
      const numAdded = params.range.endColumn - startCol + 1;
      onColumnsChangedRef.current?.({
        kind: 'insert-col',
        startColumn: startCol,
        count: numAdded
      });
      suppressTrailingColumnAlignUntilMs =
        Date.now() + SUPPRESS_TRAILING_ALIGN_AFTER_COL_INSERT_MS;
      if (skipInsertColCreateRef?.current) {
        skipInsertColCreateRef.current = false;
        addColumnInProgressRef.current = false;
        headerStateRef.current?.refreshHeaderBar?.();
        return;
      }
      addColumnInProgressRef.current = false;
      scheduleDebouncedSyncRef.current?.();
    } else if (
      colInsertExecutedIds.includes(id) &&
      id !== 'sheet.command.insert-col'
    ) {
      onColumnsChangedRef.current?.();
      addColumnInProgressRef.current = false;
      headerStateRef.current?.refreshHeaderBar?.();
      // Univer emits insert-col-by-range after insert-col; during enrichment/prioritization
      // that schedules runSpreadsheetSync → GET /columns + /cells every tick if unguarded.
      if (
        !isApplyingSyncFromBackendRef?.current &&
        !enrichmentLockedRef?.current
      ) {
        scheduleDebouncedSyncRef.current?.();
      }
      requestAnimationFrame(() => {
        requestAnimationFrame(() => {
          if (isApplyingSyncFromBackendRef?.current || !canEditRef.current)
            return;
          const st = headerStateRef.current;
          const ucc =
            univerAPI
              .getActiveWorkbook()
              ?.getActiveSheet()
              ?.getSheet?.()
              ?.getConfig?.()?.columnCount ?? 0;
          if (st?.columnNamesByIndex && st.columnNamesByIndex.length < ucc) {
            const added = reconcileHeaderColumnsToUniverCount(
              univerAPI,
              headerStateRef,
              normalizeColumnIndices
            );
            if (added > 0) {
              headerStateRef.current?.refreshHeaderBar?.();
              onColumnsChangedRef.current?.();
            }
          }
        });
      });
    } else if (id === 'sheet.command.remove-col') {
      const numRemoved = params.range.endColumn - params.range.startColumn + 1;
      const startCol = params.range.startColumn;
      columnNamesByIndex.splice(startCol, numRemoved);
      if (state?.columns) {
        state.columns.splice(startCol, numRemoved);
        normalizeColumnIndices(state.columns);
      }
      const colMap = new Map<string, CellValidationError>();
      for (const e of Array.from(validationErrorsMapRef.current.values())) {
        if (e.col < startCol) {
          colMap.set(`${e.row},${e.col}`, e);
        } else if (e.col >= startCol + numRemoved) {
          const newCol = e.col - numRemoved;
          colMap.set(`${e.row},${newCol}`, {
            ...e,
            col: newCol,
            columnName: columnNamesByIndex[newCol] ?? e.columnName
          });
        }
      }
      validationErrorsMapRef.current = colMap;
      setValidationErrorsRef.current?.(Array.from(colMap.values()));
      requestSheetRepaint?.();
      headerStateRef.current?.refreshHeaderBar?.();
      onColumnsChangedRef.current?.({
        kind: 'remove-col',
        startColumn: startCol,
        count: numRemoved
      });
      scheduleDebouncedSyncRef.current?.();
    } else if (
      id === 'sheet.command.move-cols' &&
      params?.fromRange != null &&
      params?.toRange != null
    ) {
      if (isApplyingSyncFromBackendRef?.current) {
        return;
      }
      const fromR = params.fromRange as {
        startColumn?: number;
        endColumn?: number;
      };
      const toR = params.toRange as {
        startColumn?: number;
        endColumn?: number;
      };
      const fromS = fromR.startColumn ?? 0;
      const fromE = fromR.endColumn ?? fromS;
      const insertAt = toR.startColumn ?? 0;
      const len = columnNamesByIndex.length;
      if (len === 0 || !state) {
        return;
      }
      applyColumnBlockMoveInPlace(columnNamesByIndex, fromS, fromE, insertAt);
      if (state.columns) {
        applyColumnBlockMoveInPlace(state.columns, fromS, fromE, insertAt);
        normalizeColumnIndices(state.columns);
      }
      const oldToNew = buildOldToNewColumnIndexMap(len, fromS, fromE, insertAt);
      const colMap = new Map<string, CellValidationError>();
      for (const e of Array.from(validationErrorsMapRef.current.values())) {
        const newCol = oldToNew[e.col];
        if (typeof newCol === 'number' && newCol >= 0) {
          colMap.set(`${e.row},${newCol}`, {
            ...e,
            col: newCol,
            columnName: columnNamesByIndex[newCol] ?? e.columnName
          });
        }
      }
      validationErrorsMapRef.current = colMap;
      setValidationErrorsRef.current?.(Array.from(colMap.values()));
      if (enrichingCellsRef?.current) {
        const next = new Set<string>();
        for (const k of Array.from(enrichingCellsRef.current)) {
          const colon = k.indexOf(':');
          if (colon < 0) continue;
          const row = parseInt(k.slice(0, colon), 10);
          const col = parseInt(k.slice(colon + 1), 10);
          const nc = oldToNew[col];
          if (typeof nc === 'number' && nc >= 0) next.add(`${row}:${nc}`);
        }
        enrichingCellsRef.current = next;
        setEnrichingCellsCount?.(next.size);
      }
      if (prioritizingCellsRef?.current) {
        const nextP = new Set<string>();
        for (const k of Array.from(prioritizingCellsRef.current)) {
          const colon = k.indexOf(':');
          if (colon < 0) continue;
          const row = parseInt(k.slice(0, colon), 10);
          const col = parseInt(k.slice(colon + 1), 10);
          const nc = oldToNew[col];
          if (typeof nc === 'number' && nc >= 0) nextP.add(`${row}:${nc}`);
        }
        prioritizingCellsRef.current = nextP;
        setPrioritizingCellsCount?.(nextP.size);
      }
      const skipSpreadsheetDirtyForBackendColumnReorder =
        applyBackendColumnReorderInProgressRef?.current === true;
      if (
        syncBufferRef?.current &&
        !skipSpreadsheetDirtyForBackendColumnReorder
      ) {
        const sheet = univerAPI
          .getActiveWorkbook()
          ?.getActiveSheet()
          ?.getSheet?.();
        const rowCount = sheet?.getConfig?.()?.rowCount ?? 0;
        addSpreadsheetDirtyFullSheet(
          syncBufferRef.current,
          columnNamesByIndex,
          rowCount
        );
      }
      requestSheetRepaint?.();
      headerStateRef.current?.refreshHeaderBar?.();
      onColumnsChangedRef.current?.({ kind: 'reorder-cols', oldToNew });
      if (!skipSpreadsheetDirtyForBackendColumnReorder) {
        scheduleDebouncedSyncRef.current?.();
      }
    } else if (id === 'sheet.command.insert-row' && params.range) {
      const range = params.range as { startRow: number; endRow: number };
      const firstRowId = range.startRow;
      const lastRowId = range.endRow;
      const numInserted = lastRowId - firstRowId + 1;
      if (lastRowId >= 0) {
        const next = (rowOperationPromiseRef.current ?? Promise.resolve())
          .then(() => getCells(campaignId, viewType))
          .then(({ cells }) => {
            const below = cells.filter((c) => c.row_id < firstRowId);
            const newEmptyRows: Cell[] = [];
            for (let row_id = firstRowId; row_id <= lastRowId; ++row_id) {
              for (let col = 0; col < columnNamesByIndex.length; ++col) {
                const columnName = columnNamesByIndex[col];
                if (columnName == null) continue;
                newEmptyRows.push({
                  row_id,
                  column_name: columnName,
                  cell_value: ''
                });
              }
            }
            const shifted = cells
              .filter((c) => c.row_id >= firstRowId)
              .map((c) => ({ ...c, row_id: c.row_id + numInserted }));
            let allCells: Cell[] = [...below, ...newEmptyRows, ...shifted];
            allCells = ensureDomainForRowsInPayload(
              allCells,
              columnNamesByIndex,
              univerAPI
            );
            return putCells(campaignId, allCells, viewType);
          })
          .then(() => {})
          .catch(() => {})
          .finally(() => {
            addRowInProgressRef.current = false;
          });
        rowOperationPromiseRef.current = next;
      }
    } else if (
      id === 'sheet.command.remove-row' &&
      (params.range ||
        (Array.isArray(params.ranges) && params.ranges.length > 0))
    ) {
      const ranges =
        Array.isArray(params.ranges) && params.ranges.length > 0
          ? (params.ranges as Array<{ startRow: number; endRow: number }>)
          : [params.range as { startRow: number; endRow: number }];
      const removedRowSet = new Set<number>();
      for (const r of ranges) {
        for (let row = r.startRow; row <= r.endRow; ++row)
          removedRowSet.add(row);
      }
      const removedRows = Array.from(removedRowSet).sort((a, b) => a - b);
      const maxRemovedRow =
        removedRows.length > 0 ? Math.max(...removedRows) : -1;
      if (maxRemovedRow >= 0) {
        const rowMap = new Map<string, CellValidationError>();
        for (const e of Array.from(validationErrorsMapRef.current.values())) {
          if (removedRowSet.has(e.row)) continue;
          const shift = removedRows.filter((r) => r < e.row).length;
          const newRow = e.row - shift;
          rowMap.set(`${newRow},${e.col}`, { ...e, row: newRow });
        }
        validationErrorsMapRef.current = rowMap;
        setValidationErrorsRef.current?.(Array.from(rowMap.values()));
        requestSheetRepaint?.();
        const next = (rowOperationPromiseRef.current ?? Promise.resolve())
          .then(() => getCells(campaignId, viewType))
          .then(async ({ cells }) => {
            const toDelete = cells.filter((c) => removedRowSet.has(c.row_id));
            const kept = cells.filter((c) => !removedRowSet.has(c.row_id));
            const renumbered = kept.map((c) => {
              if (c.row_id <= maxRemovedRow) return c;
              const shift = removedRows.filter((r) => r < c.row_id).length;
              return { ...c, row_id: c.row_id - shift };
            });
            const oldPositionsToRemove = cells.filter(
              (c) => c.row_id > maxRemovedRow
            );
            const allToDelete = [...oldPositionsToRemove, ...toDelete];
            if (allToDelete.length > 0) {
              try {
                await deleteCells(campaignId, allToDelete, viewType);
              } catch {
                // continue
              }
            }
            const cellsToPut = ensureDomainForRowsInPayload(
              renumbered,
              columnNamesByIndex,
              univerAPI
            );
            await putCells(campaignId, cellsToPut, viewType);
          })
          .catch(() => {});
        rowOperationPromiseRef.current = next;
      }
    } else if (id === 'sheet.command.set-range-values') {
      if (isApplyingSyncFromBackendRef?.current) {
        isApplyingValidationStylesRef.current = false;
        return;
      }
      let range = params.range as
        | {
            startRow: number;
            startColumn: number;
            endRow: number;
            endColumn: number;
          }
        | undefined;
      if (!range) {
        range = getRangeFromValue(params.value) ?? undefined;
      }
      if (!range) {
        const activeRange = univerAPI
          .getActiveWorkbook()
          ?.getActiveSheet()
          ?.getActiveRange?.();
        const rangeData =
          typeof activeRange?.getRange === 'function'
            ? activeRange.getRange()
            : null;
        if (
          rangeData &&
          typeof rangeData === 'object' &&
          'startRow' in rangeData &&
          'endRow' in rangeData
        ) {
          range = rangeData as {
            startRow: number;
            startColumn: number;
            endRow: number;
            endColumn: number;
          };
        }
      }
      if (!range || range.endRow < 0) {
        if (isApplyingValidationStylesRef.current)
          isApplyingValidationStylesRef.current = false;
        return;
      }
      const dataStartRow = range.startRow;
      if (isApplyingEnrichmentCellRef.current) {
        return;
      }
      if (isApplyingValidationStylesRef.current) {
        isApplyingValidationStylesRef.current = false;
        return;
      }
      const map = new Map(validationErrorsMapRef.current);
      for (let row = dataStartRow; row <= range.endRow; ++row) {
        for (let col = range.startColumn; col <= range.endColumn; ++col) {
          map.delete(`${row},${col}`);
        }
      }
      validationErrorsMapRef.current = map;
      setValidationErrorsRef.current?.(Array.from(map.values()));

      if (range.endRow >= 0 && dataStartRow <= range.endRow) {
        type ValueObjCell = {
          v: unknown;
          s?: Record<string, unknown> | null;
          t?: CellValueType;
        };
        const valueObj: Record<number, Record<number, ValueObjCell>> = {};
        const newErrors: CellValidationError[] = [];
        const fWorksheet = univerAPI.getActiveWorkbook()?.getActiveSheet();
        const sheet = fWorksheet?.getSheet();
        const useSheetValues = params.value == null && sheet != null;
        for (let row = dataStartRow; row <= range.endRow; ++row) {
          valueObj[row] = {};
          for (let col = range.startColumn; col <= range.endColumn; ++col) {
            let rawV: unknown;
            if (useSheetValues && sheet) {
              rawV = sheet.getCellValueOnly(row, col);
            } else {
              rawV = getCellRawValueAt(params.value, range, row, col);
            }
            const v =
              rawV != null && rawV !== '' ? normalizeCellValue(rawV) : '';
            const colType =
              getColTypeFromSchema(
                columns[col] as ColumnSchemaWithData | undefined
              ) ?? 'str';
            const result = validateCell(v, colType);
            const cellValue = normalizeCellValue(rawV ?? '');
            if (!result.valid && result.message) {
              const valueStr = cellValue;
              const err: CellValidationError = {
                row,
                col,
                columnName: columnNamesByIndex[col] ?? `Column ${col}`,
                value:
                  valueStr.slice(0, 50) + (valueStr.length > 50 ? '…' : ''),
                message: result.message
              };
              newErrors.push(err);
              map.set(`${row},${col}`, err);
            }
            valueObj[row][col] = cellValueToUniverCell(cellValue, {
              colType,
              ...(map.has(`${row},${col}`) ? {} : { s: VALID_CELL_STYLE })
            }) as ValueObjCell;
          }
        }
        validationErrorsMapRef.current = map;
        setValidationErrorsRef.current?.(Array.from(map.values()));
        requestSheetRepaint?.();
        // User edit came in via command; we're about to emit the mutation ourselves, and the
        // mutation handler will return early due to isApplyingValidationStylesRef. So record
        // dirty range and schedule sync here so edits (e.g. to enriched cells) are saved.
        if (syncBufferRef?.current) {
          addSpreadsheetDirtyRange(
            syncBufferRef.current,
            columnNamesByIndex,
            range
          );
        }
        scheduleDebouncedSyncRef.current?.();
        isApplyingValidationStylesRef.current = true;
        const rangeToApply = getRangeFromValue(valueObj) ?? range;
        univerAPI.executeCommand('sheet.mutation.set-range-values', {
          range: rangeToApply,
          cellValue: valueObj
        });
      }
    } else if (
      id === 'sheet.mutation.set-range-values' &&
      params?.cellValue !== undefined
    ) {
      let range = getRangeFromValue(params.cellValue) ?? null;
      if (!range) {
        const activeRange = univerAPI
          .getActiveWorkbook()
          ?.getActiveSheet()
          ?.getActiveRange?.();
        const rangeData =
          typeof activeRange?.getRange === 'function'
            ? activeRange.getRange()
            : null;
        if (
          rangeData &&
          typeof rangeData === 'object' &&
          'startRow' in rangeData &&
          'endRow' in rangeData
        ) {
          range = rangeData as {
            startRow: number;
            startColumn: number;
            endRow: number;
            endColumn: number;
          };
        }
      }
      if (range && range.endRow >= 0) {
        if (isApplyingValidationStylesRef.current) {
          isApplyingValidationStylesRef.current = false;
          return;
        }
        if (isApplyingEnrichmentCellRef.current) {
          return;
        }
        if (isApplyingSyncFromBackendRef?.current) {
          return;
        }
        const dataStartRow = range.startRow;
        if (syncBufferRef?.current) {
          addSpreadsheetDirtyRange(
            syncBufferRef.current,
            columnNamesByIndex,
            range
          );
        }
        scheduleDebouncedSync();
        const validationMap = new Map(validationErrorsMapRef.current);
        for (let row = dataStartRow; row <= range.endRow; ++row) {
          for (let col = range.startColumn; col <= range.endColumn; ++col) {
            validationMap.delete(`${row},${col}`);
          }
        }
        validationErrorsMapRef.current = validationMap;
        setValidationErrorsRef.current?.(Array.from(validationMap.values()));

        const validationStartRow = range.startRow;
        if (range.endRow >= 0 && validationStartRow <= range.endRow) {
          const valueObj: Record<
            number,
            Record<number, { v: unknown; s?: Record<string, unknown> | null }>
          > = {};
          for (let row = validationStartRow; row <= range.endRow; ++row) {
            valueObj[row] = {};
            for (let col = range.startColumn; col <= range.endColumn; ++col) {
              const rawV = getCellRawValueAt(params.cellValue, range, row, col);
              const v =
                rawV != null && rawV !== '' ? normalizeCellValue(rawV) : '';
              const colType =
                getColTypeFromSchema(
                  columns[col] as ColumnSchemaWithData | undefined
                ) ?? 'str';
              const result = validateCell(v, colType);
              const cellValue = normalizeCellValue(rawV ?? '');
              if (!result.valid && result.message) {
                const valueStr = cellValue;
                const err: CellValidationError = {
                  row,
                  col,
                  columnName: columnNamesByIndex[col] ?? `Column ${col}`,
                  value:
                    valueStr.slice(0, 50) + (valueStr.length > 50 ? '…' : ''),
                  message: result.message
                };
                validationMap.set(`${row},${col}`, err);
              }
              valueObj[row][col] = cellValueToUniverCell(cellValue, {
                colType,
                ...(validationMap.has(`${row},${col}`)
                  ? {}
                  : { s: VALID_CELL_STYLE })
              }) as { v: unknown; s?: Record<string, unknown> | null };
            }
          }
          validationErrorsMapRef.current = validationMap;
          setValidationErrorsRef.current?.(Array.from(validationMap.values()));
          requestSheetRepaint?.();
          isApplyingValidationStylesRef.current = true;
          const rangeToApply = getRangeFromValue(valueObj) ?? range;
          univerAPI.executeCommand('sheet.mutation.set-range-values', {
            range: rangeToApply,
            cellValue: valueObj
          });
        }
      }
    }
  });

  let debouncedSyncChain: Promise<void> = Promise.resolve();

  /** Single debounced bidirectional sync: read sheet + backend, merge by source, PUT, apply DB-source to sheet. */
  const scheduleDebouncedSync = () => {
    spreadsheetBackendSyncPhaseSetterRef?.current?.('saving');
    if (syncDebounceTimerRef.current != null) {
      clearTimeout(syncDebounceTimerRef.current);
    }
    syncDebounceTimerRef.current = setTimeout(() => {
      syncDebounceTimerRef.current = null;
      const state = headerStateRef.current;
      if (state?.campaignId) {
        const dbTriggeredSync = dbTriggeredSyncPendingRef?.current ?? false;
        if (dbTriggeredSyncPendingRef?.current) {
          dbTriggeredSyncPendingRef.current = false;
        }
        const options = {
          viewType,
          columns: state.columns,
          syncBufferRef: syncBufferRef ?? undefined,
          isApplyingSyncFromBackendRef:
            isApplyingSyncFromBackendRef ?? undefined,
          enrichmentLockedRef: enrichmentLockedRef ?? undefined,
          dbTriggeredSync,
          headerStateRef: headerStateRef ?? undefined,
          onBackendOnlyColumns: onBackendOnlyColumns ?? undefined,
          applySheetColumnOrderFromBackend:
            applySheetColumnOrderFromBackend ?? undefined,
          cellValueToUniverCell,
          onEnrichingCellsApplied:
            enrichingCellsRef && setEnrichingCellsCount
              ? (cellKeysRowCol: Set<string>) => {
                  cellKeysRowCol.forEach((k) => {
                    enrichingCellsRef.current.delete(k);
                    prioritizingCellsRef?.current?.delete(k);
                  });
                  setEnrichingCellsCount(enrichingCellsRef.current.size);
                  setPrioritizingCellsCount?.(
                    prioritizingCellsRef?.current.size ?? 0
                  );
                }
              : undefined
        };
        debouncedSyncChain = debouncedSyncChain
          .catch(() => {
            // continue
          })
          .then(() =>
            syncSheetCellsToBackend(
              state.campaignId,
              univerAPI,
              state.columnNamesByIndex,
              options
            )
              .then(() => {
                spreadsheetBackendSyncPhaseSetterRef?.current?.('saved');
              })
              .catch(() => {
                spreadsheetBackendSyncPhaseSetterRef?.current?.('idle');
              })
          );
      } else {
        spreadsheetBackendSyncPhaseSetterRef?.current?.('idle');
      }
    }, syncOnCellEditDebounceMs);
  };
  scheduleDebouncedSyncRef.current = scheduleDebouncedSync;
  const scheduleDebouncedSyncWithFullSheetFeDirty = () => {
    const state = headerStateRef.current;
    const buffer = syncBufferRef?.current;
    if (buffer && state?.columnNamesByIndex?.length) {
      const rowCount =
        univerAPI
          .getActiveWorkbook()
          ?.getActiveSheet?.()
          ?.getSheet?.()
          ?.getConfig?.()?.rowCount ?? 0;
      if (rowCount > 0)
        addSpreadsheetDirtyFullSheet(
          buffer,
          state.columnNamesByIndex,
          rowCount
        );
    }
    scheduleDebouncedSync();
  };

  /** After paste: extend header/columns if Univer widened the grid (only when `names.length < columnCount`; skip while Add Column holds the lock ref). */
  const alignTrailingColumnsAfterPasteThenFullSheetSync = () => {
    requestAnimationFrame(() => {
      requestAnimationFrame(() => {
        if (
          canEditRef.current &&
          !isApplyingSyncFromBackendRef?.current &&
          !addColumnInProgressRef.current &&
          Date.now() >= suppressTrailingColumnAlignUntilMs
        ) {
          const state = headerStateRef.current;
          const namesLen = state?.columnNamesByIndex?.length ?? 0;
          const ucc =
            univerAPI
              .getActiveWorkbook()
              ?.getActiveSheet()
              ?.getSheet?.()
              ?.getConfig?.()?.columnCount ?? 0;
          if (namesLen < ucc) {
            const added = reconcileHeaderColumnsToUniverCount(
              univerAPI,
              headerStateRef,
              normalizeColumnIndices
            );
            if (added > 0) {
              headerStateRef.current?.refreshHeaderBar?.();
              onColumnsChangedRef.current?.();
            }
          }
        }
        scheduleDebouncedSyncWithFullSheetFeDirty();
      });
    });
  };

  univerAPI.addEvent(univerAPI.Event.CommandExecuted, (e) => {
    if (!SHEET_PASTE_COMMAND_IDS.includes(e.id)) return;
    alignTrailingColumnsAfterPasteThenFullSheetSync();
  });

  univerAPI.addEvent(
    univerAPI.Event.Undo,
    scheduleDebouncedSyncWithFullSheetFeDirty
  );
  univerAPI.addEvent(
    univerAPI.Event.Redo,
    scheduleDebouncedSyncWithFullSheetFeDirty
  );
  if ('SheetRangeSorted' in univerAPI.Event) {
    univerAPI.addEvent(
      univerAPI.Event.SheetRangeSorted as 'SheetRangeSorted',
      scheduleDebouncedSyncWithFullSheetFeDirty
    );
  }
  if ('ClipboardPasted' in univerAPI.Event) {
    univerAPI.addEvent(
      univerAPI.Event.ClipboardPasted as 'ClipboardPasted',
      alignTrailingColumnsAfterPasteThenFullSheetSync
    );
  }
}

/** Create handler for CellClicked: deselect column editor when user clicks a cell. */
export function createSpreadsheetCellClickedHandler(
  ctx: SpreadsheetCellClickedContext
): (params: unknown) => void {
  return (params: unknown) => {
    const cellParams = params as { row: number; column: number };
    if (cellParams?.row == null || cellParams?.column == null) return;
    ctx.onColumnDeselect();
  };
}
